import { isBrandInNAM } from "@faire/help-center-self-serve/utils/isBrandInNAM";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import brandPortalAccountRoute from "@faire/web-api--source/routes/www/brand-portal/account";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { RowWithBullet } from "../common/RowWithBullet";

interface IUpdatePayoutsIAProps {
  brand: IBrand | undefined;
}

export const UpdatePayoutsIA: React.FC<IUpdatePayoutsIAProps> = ({ brand }) => {
  if (isBrandInNAM(brand)) {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can choose when you want to get paid after orders ship. Payouts are initiated based on the payout option selected, but may take 3-5 business days to reflect in your account, depending on your bank’s processing time. Your payment processing fee is based on your selection."
            description={{
              img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <ul>
          <RowWithBullet>
            <Typography>
              <StrictLocalMsg
                /* eslint-disable-next-line @faire/no-dollar-symbol-in-strings */
                defaultMessage="<BoldFormatting>Next day payout</BoldFormatting>: 3.5% + $0.30"
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
                }}
              />
            </Typography>
          </RowWithBullet>
          <Spacer height="1x" />
          <RowWithBullet>
            <Typography>
              <StrictLocalMsg
                /* eslint-disable-next-line @faire/no-dollar-symbol-in-strings */
                defaultMessage="<BoldFormatting>30-day payout</BoldFormatting>: 2.4% + $0.30"
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
                }}
              />
            </Typography>
          </RowWithBullet>
          <Spacer height="1x" />
          <RowWithBullet>
            <Typography>
              <StrictLocalMsg
                /* eslint-disable-next-line @faire/no-dollar-symbol-in-strings */
                defaultMessage="<BoldFormatting>60-day payout</BoldFormatting>: 1.9% + $0.30"
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
                }}
              />
            </Typography>
          </RowWithBullet>
        </ul>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="To change your payout option, and corresponding payment processing fee, go to <AccountSettingLinkFormatting>Account settings</AccountSettingLinkFormatting> and select the <PayoutLinkFormatting>Payout</PayoutLinkFormatting> tab."
            values={{
              AccountSettingLinkFormatting: (chunks: JSX.Element) =>
                PayoutsLinkFormatting(chunks, "my-account"),
              PayoutLinkFormatting: (chunks: JSX.Element) =>
                PayoutsLinkFormatting(chunks, "payment"),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="Changes will apply to all future orders placed after the payout setting is updated. Contact us to change your payout option on existing orders."
            description={{
              img: "https://cdn.faire.com/fastly/c49c0bcf85ca756228a321d50986168bf17af10ffc06712a93469f9e42cad603.png",
            }}
          />
        </Typography>
      </Column>
    );
  }
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can choose when you want to get paid after orders ship. Payouts are initiated based on the payout option selected, but may take 3-5 business days to reflect in your account, depending on your bank’s processing time. Your payment processing fee is based on your selection."
          description={{
            text: "Header of the section explaining the payout options for brands outside of North America.",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ul>
        <RowWithBullet>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>ACH Next Day</BoldFormatting>: 3% payout fee is applied to both the order and shipping costs"
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Description of the ACH Next Day payout option.",
              }}
            />
          </Typography>
        </RowWithBullet>
        <Spacer height="1x" />
        <RowWithBullet>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>ACH 30-day</BoldFormatting>: no payout fee "
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Description of the ACH 30-day payout option.",
              }}
            />
          </Typography>
        </RowWithBullet>
      </ul>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To change your payout option, and corresponding payment processing fee, go to <AccountSettingLinkFormatting>Account settings</AccountSettingLinkFormatting> and select the <PayoutLinkFormatting>Payout</PayoutLinkFormatting> tab."
          values={{
            AccountSettingLinkFormatting: (chunks: JSX.Element) =>
              PayoutsLinkFormatting(chunks, "my-account"),
            PayoutLinkFormatting: (chunks: JSX.Element) =>
              PayoutsLinkFormatting(chunks, "payment"),
          }}
          description={{
            text: "Description of how to change the payout option.",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Changes will apply to all future orders placed after the payout setting is updated. Contact us to change your payout option on existing orders."
          description={{
            text: "Description of how to change the payout option on existing orders.",
          }}
        />
      </Typography>
    </Column>
  );
};

const PayoutsLinkFormatting = (chunks: JSX.Element, tab: string) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalAccountRoute({ tab })}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
